import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Utensils, Loader2, Search, X, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { usePublicFoodItems } from '@/features/food/useFoodItems';
import { useFoodCategories } from '@/features/food/useFoodCategories';
import { getFoodPhotoPublicUrl } from '@/features/food/photoStorage';
import { useDocumentMeta } from '@/lib/useDocumentMeta';
import PublicPageHero from '@/components/PublicPageHero';
import SmartImage from '@/components/SmartImage';
import { IMAGE_SLOT, FOCAL } from '@/lib/imageSlots';
import { MENU_HERO, HERO_OBJECT_POSITION } from '@/data/marketing/photos';
import { fadeUp, staggerContainer } from '@/lib/animations';
import { cn } from '@/lib/utils';
import type { FoodItemWithRelations } from '@/features/food/types';

/**
 * Page Bouffe publique — vitrine du menu nourriture.
 * Mirror de PublicCocktails mais pour food_items :
 *   - Hero
 *   - Recherche + pills catégorie
 *   - Sections par catégorie avec cards (photo + nom + description + prix + allergènes)
 *   - Seulement les items is_public=true
 */
export default function PublicFood() {
  useDocumentMeta({
    title: "Menu Bouffe — Bar L'Alambic",
    description:
      "Notre menu bouffe : tartares, plateaux à partager, pizzas, grilled cheese, fromages locaux et plus. Bar L'Alambic à La Sarre.",
  });

  const { data: rawItems = [], isLoading } = usePublicFoodItems();
  const { data: categories = [] } = useFoodCategories();

  // Filtre out les "Extras" du menu public — ce sont des add-ons,
  // pas des items du menu (ex: "extra fromage", "extra pacanes").
  const extrasCategoryId = useMemo(
    () =>
      categories.find((c) => c.label.toLowerCase() === 'extras')?.id ?? null,
    [categories],
  );
  const items = useMemo(() => {
    if (!extrasCategoryId) return rawItems;
    return rawItems.filter((it) => it.category_id !== extrasCategoryId);
  }, [rawItems, extrasCategoryId]);

  const [search, setSearch] = useState('');
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const low = search.trim().toLowerCase();
    return items.filter((it) => {
      if (activeCategoryId && it.category_id !== activeCategoryId) return false;
      if (low && !it.name.toLowerCase().includes(low)) return false;
      return true;
    });
  }, [items, search, activeCategoryId]);

  // Catégories utilisées par les items publics (autres cachées des pills)
  const usedCategoryIds = useMemo(() => {
    const s = new Set<string>();
    for (const it of items) if (it.category_id) s.add(it.category_id);
    return s;
  }, [items]);

  const visibleCategories = categories.filter(
    (c) => c.is_public && usedCategoryIds.has(c.id),
  );

  // Items groupés par catégorie pour affichage en sections
  const itemsByCategory = useMemo(() => {
    const m = new Map<string, FoodItemWithRelations[]>();
    for (const it of filtered) {
      const key = it.category_id ?? '__none__';
      const list = m.get(key) ?? [];
      list.push(it);
      m.set(key, list);
    }
    return m;
  }, [filtered]);

  return (
    <>
      <PublicPageHero
        photo={MENU_HERO}
        objectPosition={HERO_OBJECT_POSITION[MENU_HERO]}
        eyebrow={
          <>
            <Utensils className="h-3 w-3" />
            Menu Bouffe
          </>
        }
        title={
          <>
            Tartares, plateaux <span className="italic text-[#D4A574]">à partager</span>
          </>
        }
        description="Bouffe d'accompagnement pensée pour la dégustation. Tartare frais, fromages d'ici, plateaux signature."
      />

      {/* Recherche + pills catégorie */}
      <section className="sticky top-0 z-10 bg-[#0A0A0A]/95 backdrop-blur border-b border-[#D4A574]/15 shadow-[0_8px_24px_-12px_rgba(212,165,116,0.15)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-4 space-y-3">
          {visibleCategories.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap justify-center md:justify-start">
              <CategoryChip
                label="Tout"
                active={activeCategoryId === null}
                onClick={() => setActiveCategoryId(null)}
                count={items.length}
              />
              {visibleCategories.map((c) => {
                const count = items.filter((it) => it.category_id === c.id).length;
                return (
                  <CategoryChip
                    key={c.id}
                    label={`${c.icon ? `${c.icon} ` : ''}${c.label}`}
                    active={activeCategoryId === c.id}
                    onClick={() =>
                      setActiveCategoryId((v) => (v === c.id ? null : c.id))
                    }
                    count={count}
                  />
                );
              })}
            </div>
          )}
          <div className="relative max-w-md mx-auto md:mx-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un plat…"
              className="pl-9 pr-9 bg-[#0A0A0A] border-[#2A2A2A] focus-visible:border-[#D4A574]/50 focus-visible:ring-[#D4A574]/20"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded-md text-neutral-500 hover:text-[#E8E0D6] hover:bg-[#1F1F1F]"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-neutral-400">
            <Loader2 className="h-6 w-6 animate-spin mr-2 text-[#D4A574]" />
            Chargement du menu…
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-[#1F1F1F] bg-[#141414] p-10 text-center">
            <Utensils className="h-12 w-12 text-neutral-600 mx-auto mb-3" />
            <p className="text-base text-neutral-400">
              {items.length === 0
                ? 'Aucun plat au menu pour le moment.'
                : 'Aucun plat ne correspond à ta recherche.'}
            </p>
          </div>
        ) : (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer(0.05, 0.05)}
            className="space-y-12"
          >
            {visibleCategories.map((cat) => {
              const list = itemsByCategory.get(cat.id);
              if (!list || list.length === 0) return null;
              return (
                <section key={cat.id}>
                  <motion.div variants={fadeUp} className="mb-5">
                    <h2 className="font-display text-3xl text-[#D4A574]">
                      {cat.icon ? `${cat.icon} ` : ''}
                      {cat.label}
                    </h2>
                  </motion.div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {list.map((it) => (
                      <FoodCard key={it.id} item={it} />
                    ))}
                  </div>
                </section>
              );
            })}
          </motion.div>
        )}
      </main>
    </>
  );
}

function CategoryChip({
  label,
  active,
  onClick,
  count,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  count: number;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ scale: 1.05, y: -1 }}
      whileTap={{ scale: 0.96 }}
      className={cn(
        'inline-flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm font-medium transition-colors',
        active
          ? 'border-[#D4A574] bg-[#D4A574]/15 text-[#D4A574]'
          : 'border-[#2A2A2A] bg-[#0A0A0A] text-neutral-400 hover:border-[#D4A574]/40',
      )}
    >
      <span>{label}</span>
      <span className="text-[10px] tabular-nums opacity-70">{count}</span>
    </motion.button>
  );
}

function FoodCard({ item }: { item: FoodItemWithRelations }) {
  const photoUrl = getFoodPhotoPublicUrl(item.photo_path, {
    width: 600,
    height: 600,
    quality: 80,
  });

  return (
    <motion.div
      variants={fadeUp}
      whileHover={{ y: -4, transition: { duration: 0.25 } }}
      className="group rounded-2xl border border-[#1F1F1F] bg-gradient-to-b from-[#161616] to-[#101010] overflow-hidden hover:border-[#D4A574]/40 hover:shadow-2xl hover:shadow-[#D4A574]/10 transition-all"
    >
      <SmartImage
        src={photoUrl}
        alt={item.name}
        ratio={IMAGE_SLOT.publicCard}
        position={FOCAL.default}
        hoverZoom
        imgClassName="[filter:drop-shadow(0_6px_14px_rgba(0,0,0,0.55))]"
        fallback={<Utensils className="h-12 w-12 text-neutral-700" />}
      />
      <div className="p-4 space-y-2">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="font-display text-lg text-[#E8E0D6] leading-tight">
            {item.name}
          </h3>
          {item.price !== null && (
            <div className="shrink-0 text-[#D4A574] font-display text-xl tabular-nums">
              {item.price.toFixed(2)} $
            </div>
          )}
        </div>

        <p className="text-[11px] text-neutral-500">
          {item.portions && `${item.portions} · `}
          {item.category?.label ?? ''}
        </p>

        {item.description && (
          <p className="text-sm text-neutral-300 leading-snug">
            {item.description}
          </p>
        )}

        {item.allergens.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {item.allergens.map((a) => (
              <span
                key={a}
                className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400/80 border border-amber-500/20 inline-flex items-center gap-1"
              >
                <AlertTriangle className="h-2.5 w-2.5" />
                {a}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
